// src/components/auth/AuthProvider.tsx

import React, { useEffect } from "react";
import { AuthLoading } from "@/components/auth/AuthLoading";
import { AuthContext, type AuthContextType } from "./AuthContext";
import { AuthReduxSync } from "@/components/auth/AuthReduxSync";
import { AuthServiceProvider, useAuthService, useAuthState } from "@/services/auth";
import { useQueryClient } from "@tanstack/react-query";
import type { User } from "@/types/auth.types";
import { metricsQueryKeys } from "@/hooks/useMetricsCatalog";
import { fetchMetricsCatalog } from "@/services/metrics";
import { categoriesTreeQueryOptions, categoriesFlatQueryOptions } from "@/services/queries/dashboards/categoriesQueries";
import {
  transactionsQueryOptions,
  portfolioHoldingsQueryOptions,
  realizedPnlQueryOptions,
  portfolioNavQueryOptions,
  portfolioPerformanceVsBenchmarkQueryOptions,
} from "@/services/queries/portfolioQueries";
import {
  portfolioCurrentQueryOptions,
  alertPoliciesQueryOptions,
  alertEventsQueryOptions,
} from "@/services/queries/riskQueries";

interface AuthProviderWrapperProps {
  children: React.ReactNode;
}

/**
 * Inner component that consumes AuthService and provides compatibility layer
 * This wraps the new AuthService with the old AuthContext API for backward compatibility
 */
const AuthProviderWrapper: React.FC<AuthProviderWrapperProps> = ({
  children,
}) => {
  const authService = useAuthService();
  const { isAuthenticated, isLoading, isLoggingOut, user } = useAuthState();
  const queryClient = useQueryClient();

  // Wrap login method
  const loginWithRedirect = async (): Promise<void> => {
    await authService.login();
  };

  // Wrap logout method
  const logout = () => {
    console.log("[AuthProvider] Logging out");
    // Call Auth0 logout (it will redirect to the returnTo URL which is /)
    authService.logout();
  };

  // Wrap getToken method (for backward compatibility)
  const getAccessTokenSilently = async (): Promise<string> => {
    return await authService.getToken();
  };

  // Wrap refreshUser method
  const refreshUser = async (): Promise<User | null> => {
    return await authService.refreshUser();
  };

  // Wrap updateUserData method
  const updateUserData = (userData: Partial<User>): void => {
    authService.updateUserData(userData);
  };

  // Prefetch Portfolio Overview queries on login (deferred and throttled to reduce contention)
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    const schedule = (fn: () => void) => {
      // Prefer idle callback when available; fallback to short timeout
      if (typeof (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number }).requestIdleCallback === 'function') {
        (window as unknown as { requestIdleCallback: (cb: () => void, opts?: { timeout?: number }) => number }).requestIdleCallback(fn, { timeout: 2000 });
      } else {
        setTimeout(fn, 300);
      }
    };

    const run = async () => {
      try {
        const token = await authService.getToken();
        if (!token || cancelled) return;

        // Batch tasks in small groups to limit concurrency pressure
        const batches: Array<Array<Promise<unknown>>> = [
          [
            queryClient.prefetchQuery(portfolioNavQueryOptions(token)),
            queryClient.prefetchQuery(portfolioPerformanceVsBenchmarkQueryOptions(token)),
          ],
          [
            queryClient.prefetchQuery(portfolioHoldingsQueryOptions(token)),
            queryClient.prefetchQuery(transactionsQueryOptions(token)),
          ],
          [
            queryClient.prefetchQuery(realizedPnlQueryOptions(token)),
            queryClient.prefetchQuery(portfolioCurrentQueryOptions(token)),
          ],
          [
            queryClient.prefetchQuery(alertPoliciesQueryOptions(token)),
            queryClient.prefetchQuery(alertEventsQueryOptions({ authToken: token, limit: 100 })),
          ],
          [
            // Prefetch metrics catalog early
            queryClient.prefetchQuery({ queryKey: metricsQueryKeys.list(), queryFn: ({ signal }) => fetchMetricsCatalog(signal), staleTime: 5 * 60 * 1000 }),
            // Prefetch categories (tree + flat)
            queryClient.prefetchQuery(categoriesTreeQueryOptions()),
            queryClient.prefetchQuery(categoriesFlatQueryOptions()),
          ]
        ];

        for (const batch of batches) {
          if (cancelled) return;
          await Promise.all(batch.map((p) => p.catch(() => undefined)));
        }
      } catch {
        // ignore prefetch errors
      }
    };

    schedule(run);

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authService, queryClient]);

  // Show loading state during initialization
  if (isLoading) {
    return <AuthLoading />;
  }

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    isLoggingOut,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    refreshUser,
    updateUserData,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      <AuthReduxSync />
      {children}
    </AuthContext.Provider>
  );
};

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Main AuthProvider component
 *
 * This is the top-level auth component that wraps the entire app.
 * It now uses AuthServiceProvider (abstraction layer) instead of Auth0Provider directly.
 *
 * Migration path:
 * - Currently uses Auth0AuthService by default
 * - To switch to Supabase: Just change the service in AuthServiceProvider
 *
 * @example
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return (
    <AuthServiceProvider>
      <AuthProviderWrapper>{children}</AuthProviderWrapper>
    </AuthServiceProvider>
  );
};
